import * as React from 'react'
import * as z from 'zod'

import {
  getSquareWidth,
  getSquareXPosFromAlign,
  getSquareBB,
  pointIsWithinBB,
  drawCheckbox,
} from './utils'

import {
  BooleanEmpty,
  BooleanIndeterminate,
  CustomCell,
  CustomRenderer,
  GridCellKind,
  ProvideEditorComponent,
  Rectangle,
  booleanCellIsEditable,
} from '@glideapps/glide-data-grid'

const defaultCellMaxSize = 15

function isOverEditableRegion(e: {
  readonly cell: BooleanCell
  readonly posX: number
  readonly posY: number
  readonly bounds: Rectangle
  readonly theme: any
}): boolean {
  const { cell, posX: pointerX, posY: pointerY, bounds, theme } = e
  const { width, height, x: cellX, y: cellY } = bounds
  const maxWidth = cell.data.maxSize ?? defaultCellMaxSize
  const cellCenterY = Math.floor(bounds.y + height / 2)
  const checkBoxWidth = getSquareWidth(
    maxWidth,
    height,
    theme.cellVerticalPadding
  )
  const posX = getSquareXPosFromAlign(
    'left',
    cellX,
    width,
    theme.cellHorizontalPadding,
    checkBoxWidth
  )
  const bb = getSquareBB(posX, cellCenterY, checkBoxWidth)
  const checkBoxClicked = pointIsWithinBB(
    cellX + pointerX,
    cellY + pointerY,
    bb
  )

  if (cell.readonly) return false

  return checkBoxClicked
}

export const booleanCellSchema = z.object({
  kind: z.literal('boolean-cell'),
  value: z.boolean(),
  maxSize: z.number().optional(),
  hoverEffectIntensity: z.number().optional(),
})

type BooleanCellProps = z.infer<typeof booleanCellSchema>
export type BooleanCell = CustomCell<BooleanCellProps>

export const BooleanCellRenderer: CustomRenderer<BooleanCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell): cell is BooleanCell =>
    (cell.data as any).kind === 'boolean-cell',
  needsHover: true,
  needsHoverPosition: true,
  measure: () => 50,
  provideEditor: () => ({
    editor: Editor,
    disablePadding: true,
    disableStyling: true,
  }),
  draw: (a) => {
    const { kind: _, value, ...rest } = a.cell.data
    const data = {
      ...rest,
      data: value,
      kind: GridCellKind.Boolean as const,
      allowOverlay: false as const,
    }

    drawBoolean(
      a,
      a.cell.data.value,
      booleanCellIsEditable(data),
      a.cell.data.maxSize ?? defaultCellMaxSize,
      a.cell.data.hoverEffectIntensity ?? 0.35
    )
  },
  onDelete: (c) => ({ ...c, value: false }),
  onSelect: (e) => {
    if (isOverEditableRegion(e)) {
      e.preventDefault()
    }
  },
  onClick: (e) => {
    if (e.cell.readonly) {
      e.preventDefault()
      return undefined
    }

    if (isOverEditableRegion(e)) {
      const toggledValue = toggleBoolean(e.cell.data.value) ?? true

      return {
        data: { ...e.cell.data, value: toggledValue },
        kind: GridCellKind.Custom as const,
        allowOverlay: true,
        copyData: toggledValue.toString(),
      }
    }

    return undefined
  },
  onPaste: (toPaste, cell) => {
    let newVal: boolean | BooleanEmpty | BooleanIndeterminate = BooleanEmpty
    if (toPaste.toLowerCase() === 'true') {
      newVal = true
    } else if (toPaste.toLowerCase() === 'false') {
      newVal = false
    } else if (toPaste.toLowerCase() === 'indeterminate') {
      newVal = BooleanIndeterminate
    }
    return newVal === cell.value ? undefined : { ...cell, data: newVal }
  },
}

function drawBoolean(
  args: any,
  data: boolean | BooleanEmpty | BooleanIndeterminate,
  canEdit: boolean,
  maxSize: number,
  hoverEffectIntensity: number
) {
  if (!canEdit && data === BooleanEmpty) {
    return
  }
  const { ctx, hoverAmount, theme, rect, highlighted, cell } = args
  const { x, y, width: w, height: h } = rect

  // Don't set the global alpha unnecessarily
  let shouldRestoreAlpha = false
  if (hoverEffectIntensity > 0) {
    let alpha = canEdit
      ? 1 - hoverEffectIntensity + hoverEffectIntensity * hoverAmount
      : 0.4
    if (data === BooleanEmpty) alpha *= hoverAmount
    if (alpha === 0) return

    if (alpha < 1) {
      shouldRestoreAlpha = true
      ctx.globalAlpha = alpha
    }
  }

  const hoverX = cell.readonly ? Infinity : args.hoverX
  const hoverY = cell.readonly ? Infinity : args.hoverY

  drawCheckbox(
    ctx,
    theme,
    data,
    x,
    y,
    w,
    h,
    highlighted,
    canEdit ? hoverX : 0,
    canEdit ? hoverY : 0,
    maxSize,
    'left'
  )

  if (shouldRestoreAlpha) {
    ctx.globalAlpha = 1
  }
}

/**
 * Checkbox behavior:
 *
 * true + click -> unchecked
 * false + click -> checked
 * indeterminate + click -> checked
 * empty + click -> checked
 */
export function toggleBoolean(
  data: boolean | null | undefined
): boolean | null | undefined {
  return data !== true
}

const Editor: ProvideEditorComponent<BooleanCell> = (cell) => {
  React.useEffect(() => {
    cell.onFinishedEditing({
      ...cell.value,
      data: {
        ...cell.value.data,
        value: toggleBoolean(cell.value.data.value) ?? true,
      },
      kind: GridCellKind.Custom as const,
      allowOverlay: true,
      copyData: `${toggleBoolean(cell.value.data.value) ?? true}`,
    })
  }, [cell])

  return null
}
